import type { LiveCheckFixture } from './types';

/**
 * Live Check fixtures, cross-checked against the four wireframes
 * (LiveCheck · the form, states, the report, the printed report). The good
 * answer is Terence Love on Delta Dental of Texas.
 */
export const LIVE_CHECK: LiveCheckFixture = {
  today: '2026-10-13',

  // Ranked by this practice's own claim volume, then alphabetically.
  payers: [
    { id: '94276', name: 'Delta Dental of Texas', volume: 2881 },
    { id: '60054', name: 'Aetna', volume: 1744 },
    { id: '62308', name: 'Cigna', volume: 1203 },
    { id: 'SLF01', name: 'Sunlife', volume: 988 },
    { id: '65978', name: 'MetLife', volume: 742 },
    { id: 'DDCA1', name: 'Delta Dental of California', volume: 410 },
    { id: 'DDNAT', name: 'Delta Dental National', volume: 365 },
    { id: 'DDINS', name: 'Delta Dental Insurance Co.', volume: 221 },
    { id: 'UHC01', name: 'UnitedHealthcare', volume: 180 },
    { id: 'TXMED', name: 'Texas Medicaid · DentaQuest', volume: 211 },
    { id: 'GUARD', name: 'Guardian', volume: 143 },
    { id: 'HUM01', name: 'Humana', volume: 96 },
  ],

  cdtCatalog: [
    { code: 'D0120', name: 'Periodic exam' },
    { code: 'D0150', name: 'Comprehensive exam' },
    { code: 'D0210', name: 'Full-mouth series' },
    { code: 'D0220', name: 'Periapical, first film' },
    { code: 'D0274', name: 'Bitewings, four' },
    { code: 'D1110', name: 'Cleaning, adult' },
    { code: 'D1206', name: 'Fluoride varnish' },
    { code: 'D1351', name: 'Sealant' },
    { code: 'D2392', name: 'Composite, 2 surfaces' },
    { code: 'D2740', name: 'Crown, porcelain/ceramic' },
    { code: 'D2950', name: 'Core build-up' },
    { code: 'D4341', name: 'SRP, per quadrant' },
    { code: 'D4910', name: 'Periodontal maintenance' },
    { code: 'D6010', name: 'Implant placement' },
    { code: 'D9944', name: 'Occlusal guard' },
  ],

  defaultProvider: { name: 'Dr. Olivia Brooks', npi: '1942887301' },

  prefilled: {
    payerId: '94276',
    payerName: 'Delta Dental of Texas',
    memberId: 'DDTX-88241907',
    group: '40219',
    firstName: 'Terence',
    lastName: 'Love',
    dob: '1988-03-12',
    procedures: [
      { code: 'D0150', name: 'Comprehensive exam' },
      { code: 'D0274', name: 'Bitewings, four' },
      { code: 'D1110', name: 'Cleaning, adult' },
    ],
  },

  checkedToday: [
    { id: 'LC-20261013-0042', name: 'Terence Love', payer: 'Delta Dental', at: '9:42 AM', outcome: 'Active' },
    { id: 'lc-0041', name: 'Marisol Vega', payer: 'Cigna', at: '9:16 AM', outcome: 'Active' },
    { id: 'lc-0040', name: 'Dwayne Patterson', payer: 'Aetna', at: '8:58 AM', outcome: 'Not found' },
    { id: 'lc-0039', name: 'Nora Ellis', payer: 'MetLife', at: '8:31 AM', outcome: 'Terminated' },
    { id: 'lc-0038', name: 'Priya Raman', payer: 'Sunlife', at: '8:12 AM', outcome: 'Active' },
  ],

  states: [
    {
      kind: 'member_not_found',
      badge: 'No match', badgeTone: 'warn',
      headline: 'Member not found',
      explanation: 'Delta Dental has no member with ID DDTX-88241907 and date of birth 03/12/1988. Nearly always a typo, a card that belongs to a different family member, or a plan that has moved to a new ID. The payer does not say which.',
      detail: [
        { label: 'What we sent', value: 'Member DDTX-88241907' },
        { label: 'Date of birth', value: 'Mar 12, 1988' },
        { label: 'Payer response', value: 'AAA 72 · subscriber/insured not found' },
        { label: 'At', value: '9:42:19 AM' },
      ],
      at: '9:42:19 AM',
      actions: [
        { label: 'Edit and run again', variant: 'primary', focusField: 'member' },
        { label: 'Re-read the card' },
        { label: 'Open the payer portal' },
      ],
    },
    {
      kind: 'terminated',
      badge: 'Terminated', badgeTone: 'neutral',
      headline: 'Coverage has ended',
      explanation: 'This plan terminated on Jun 30, 2026. The patient was active before that date. Treat the visit as self-pay unless the patient has new cover. If they have changed jobs or plans recently, ask for the new card and run the check again.',
      detail: [
        { label: 'Plan', value: 'Delta Dental PPO · group 40219' },
        { label: 'Terminated', value: 'Jun 30, 2026' },
        { label: 'Last active', value: 'Jun 30, 2026' },
        { label: 'At', value: '9:42:19 AM' },
      ],
      at: '9:42:19 AM',
      actions: [
        { label: 'Check a different plan', variant: 'primary' },
        { label: 'Print this result' },
      ],
    },
    {
      kind: 'payer_unreachable',
      badge: 'Try again', badgeTone: 'warn',
      headline: 'The payer is not answering',
      explanation: "Delta Dental's eligibility service did not respond. Nothing is wrong with what you entered.",
      detail: [
        { label: 'Payer', value: 'Delta Dental of Texas · 94276' },
        { label: 'At', value: '9:42:19 AM' },
      ],
      at: '9:42:19 AM',
      actions: [
        { label: 'Retry now', variant: 'primary' },
        { label: 'Retry in the background' },
        { label: 'Open the payer portal' },
      ],
    },
    {
      kind: 'partial',
      badge: 'Partial', badgeTone: 'warn',
      headline: 'Answered, but thinly',
      explanation: 'The payer confirmed active coverage but returned no maximums, no deductible and no frequencies. Some payers answer the minimum electronically. ELVA will read the benefit summary from the payer portal and fill the gaps — usually within a few minutes. The report opens now with what is known and says plainly what is missing.',
      detail: [
        { label: 'Confirmed', value: 'Active since Jan 1, 2025' },
        { label: 'Missing', value: 'Maximum, deductible, frequencies' },
        { label: 'Next', value: 'Portal download queued' },
        { label: 'At', value: '9:42:19 AM' },
      ],
      at: '9:42:19 AM',
      actions: [
        { label: 'Open the report', variant: 'primary' },
        { label: 'Get the rest from the portal' },
      ],
    },
    {
      kind: 'manual_required',
      badge: 'Manual', badgeTone: 'warn',
      headline: 'This payer needs a person',
      explanation: 'Your settings mark Texas Medicaid · DentaQuest as requiring manual verification, so no electronic check was sent. This is the manual_eligibility setting in AI Dispatch Policies. Verify through the portal or by phone, then attach the result here so the report is still produced.',
      detail: [
        { label: 'Payer', value: 'Texas Medicaid · DentaQuest' },
        { label: 'Set by', value: 'Ava Thompson, Aug 2026' },
        { label: 'Reason', value: 'Portal-only payer' },
        { label: 'Provider line', value: '(800) 555-0144' },
      ],
      at: '9:42:19 AM',
      actions: [
        { label: 'Open the payer portal', variant: 'primary' },
        { label: 'Record what you were told' },
      ],
    },
    {
      kind: 'payer_unresolved',
      badge: 'Payer unresolved', badgeTone: 'warn',
      headline: 'We cannot tell which payer this is',
      explanation: '"Delta Dental" matches six payers, and they do not share eligibility systems. This is the commonest failed check of all, and it is why the payer field is a list and not a text box. The card usually names the plan and the state on the back.',
      detail: [
        { label: 'On the card', value: 'Delta Dental' },
        { label: 'Candidates', value: 'Texas, National, California, Insurance Co., +2' },
        { label: 'Best guess', value: 'Delta Dental of Texas · 94276' },
        { label: 'Based on', value: '78% of your Delta claims' },
      ],
      at: '9:42:19 AM',
      actions: [
        { label: 'Choose the payer', variant: 'primary', focusField: 'payer' },
        { label: 'Re-read the card' },
      ],
    },
  ],

  report: {
    checkId: 'LC-20261013-0042',
    snapshotId: 'LC-20261013-0042',
    patientName: 'Terence Love',
    demographics: 'Male · 38 · born Mar 12, 1988',
    isPatient: false,
    checkedAt: 'Today, 9:42 AM',
    coverage: {
      status: 'Active',
      plan: 'Delta Dental of Texas · PPO · in-network',
      effective: 'Effective Jan 1, 2025 · no end date',
      source: 'Stedi',
    },
    annualMax: { left: 1340, used: 660, max: 2000, resets: '2026-12-31', source: 'Stedi' },
    deductible: { line1: '$50 · met', line2: 'Family $150, $100 met', source: 'Stedi' },
    member: { id: 'DDTX-88241907', line: 'Group 40219 · subscriber is the patient · Payer ID 94276' },
    noHistory: true,
    quoted: [
      { code: 'D2740', name: 'Crown, porcelain/ceramic', covered: 'Major · 50%', officeFee: 850, allowed: 700, insurance: 350, limits: '1 per tooth every 5 years. No last-done date — the payer did not report prior use and this office has no history.', source: ['Stedi'] },
      { code: 'D2950', name: 'Core build-up', covered: 'Bundled', officeFee: 100, allowed: 70, insurance: 0, limits: 'Not separately payable with a crown on this payer — bundled into D2740. The allowed amount still caps what the patient can be charged.', source: ['Payer Manual'] },
      { code: 'D0220', name: 'Periapical, first film', covered: 'Preventive · 100%', officeFee: 31, allowed: 31, insurance: 31, limits: '1 per date of service.', source: ['Stedi'] },
      { code: 'D0150', name: 'Comprehensive exam', covered: 'Preventive · 100%', officeFee: 95, allowed: 68, insurance: 68, limits: 'Once every 3 years. Shares an allowance with D0120.', source: ['Stedi'] },
      { code: 'D1110', name: 'Cleaning, adult', covered: 'Preventive · 100%', officeFee: 145, allowed: 98, insurance: 98, limits: '2 per calendar year. Used this year: not reported.', source: ['Stedi'] },
      { code: 'D4341', name: 'SRP, per quadrant', covered: 'Basic · 80%', officeFee: 320, allowed: 245, insurance: 196, limits: 'Frequency stated as “per ADA guidelines” — no number, so no remaining count is shown. Perio charting must be attached to the claim.', source: ['Payer Manual'] },
    ],
    rulesTotal: 61,
    coverageRows: [
      { service: 'Preventive', subLabel: 'Cleanings, exams, X-rays', covered: '100%', rules: 'Deductible waived · does not count toward the maximum', forPatient: 'Applies', sources: ['Stedi'] },
      { service: 'Basic', subLabel: 'Fillings, simple extractions', covered: '80%', rules: 'Deductible applies', forPatient: 'Applies', sources: ['Stedi'] },
      { service: 'Periodontics', covered: '80%', rules: 'Perio charting required on the claim', forPatient: 'Applies', sources: ['Stedi', 'Payer Manual'] },
      { service: 'Major', subLabel: 'Crowns, bridges, root canals', covered: '50%', rules: 'Build-ups bundled · crowns pay on the seat date', forPatient: 'Applies', sources: ['Stedi', 'Payer Manual'] },
      { service: 'Orthodontics', covered: '50%', rules: '$2,000 lifetime · adults included on this plan', forPatient: '$2,000 left', sources: ['Stedi'] },
      { service: 'Implants', covered: 'Not covered', rules: 'Plan exclusion', forPatient: 'Patient pays in full', sources: ['Payer Portal'] },
    ],
    rules: [
      { rule: 'Build-up bundled into the crown', meaning: 'D2950 pays $0. The patient owes the allowed $70.00 for it, not your $100.00 office fee.', howKnown: '9 of 9 of your claims zero-paid, CARC 97 · Your claims' },
      { rule: 'Crowns pay on the seat date', meaning: 'The crown is billed when it is seated, not when it is prepped, so the patient owes then.', howKnown: '23 of 23 of your claims paid against the seat date · Your claims' },
      { rule: 'Perio charting on the claim', meaning: 'D4341 is held until the charting is attached.', howKnown: 'Delta Dental provider manual 2026, §7.4 · Payer manual' },
      { rule: 'SRP frequency', meaning: 'Stated as "per ADA guidelines", with no figure. No remaining count is shown for D4341.', howKnown: 'Provider manual 2026, §7.4 · Payer manual', badge: 'needs_clarification' },
    ],
    rulesLink: 'All 61 rules ELVA holds for this payer: Settings › Payers › Delta Dental',
    unknown: [
      { title: 'How much of the frequencies has been used', detail: 'The payer did not report prior use, and this person has no history at your office. Cleanings, exams and the crown are quoted as if available; if they were done elsewhere this year, the payer may pay less.', source: 'Stedi' },
      { title: 'SRP frequency', detail: 'The manual states a standard, not a number. ELVA prints the standard and never converts it into a count.', source: 'Payer Manual' },
      { title: 'Secondary coverage', detail: 'None reported by the payer. If the patient has a second plan, run it as its own check.', source: 'Stedi' },
    ],
    sources: [
      { source: 'Stedi', when: 'Today, 9:42 AM', gave: 'Active coverage, maximum and what is left, deductible, 14 service benefits.', reference: 'Payer 94276 · trace DD-4471802' },
      { source: 'Historical data', when: 'Synced today, 5:40 AM', gave: '2 rules for this payer, from how it has paid this office.', reference: '2,881 Delta Dental claims, 24 months' },
      { source: 'Payer Manual', when: 'Read Sep 4, 2026', gave: '4 rules apply to this plan.', reference: 'Delta Dental 2026 provider manual §7.4, §9.1' },
      { source: 'Payer Portal', when: '—', gave: 'Not needed — the electronic check answered everything required.', reference: '—' },
      { source: 'AI Call', when: '—', gave: 'Not used.', reference: '—' },
    ],
    storedNote: 'This result is stored as snapshot LC-20261013-0042. If a claim is later denied, this is what the appeal cites. Kept for 90 days unless attached to a patient.',
  },
};
